import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from '@sharedComponents/DesignSystem/Inputs';
import { MultipleRemoveButton } from '@sharedComponents/Field/Elements/MultipleFormGroup/MultipleRemoveButton';
import cx from 'classnames';

import {
  UpdateCurrentSubmissionOriginSectionNameActionPayloadType,
  OriginSectionEntityBlocksOriginBlockEntityInterface,
  OriginBlockEntityInterface,
} from '@types';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button as MuiButton,
  FormGroup,
  TextField,
} from '@material-ui/core';
import { Add, ExpandMore } from '@material-ui/icons';

import { DragDrop as DragDropArea, DragResult } from '@components/SharedComponents/DragDrop';
import { DragDrop, EditSmallIcon, TrashIcon, WellDone } from '@images/icons';
import { translate } from '@utils';
import {
  createSubmissionOriginSectionBlock,
  deleteCurrentSubmissionOriginSectionBlock,
  deleteCurrentSubmissionOriginSection,
  updateCurrentSubmissionOriginSectionBlock,
  updateCurrentSubmissionOriginSectionBlocksOrder,
  updateCurrentSubmissionOriginSectionName,
} from '@actions/submissionActions';

import {
  FormValues as OriginSectionEditorFormValues,
  OriginSectionEditorForm,
} from '../OriginSectionEditorForm';

import './index.scss';
import { OriginBlockFormValueKey } from '@enums';
import { OriginSectionEntityInterface } from '@types';

const formContentClasses = {
  root: 'section-form-wrapper',
};

interface props {
  originSection: OriginSectionEntityInterface;
  originSectionWithOrderId: string;
  originSectionOrder: number;
  onSectionAccordionOpen?: (isOpened: boolean, sectionWithOrderId) => void;
}

const boxStyles = { display: 'flex' };

// This class is used for e2e tests, because there is no other way to set data-testid
const accordionClasses = { content: 'template-section-content' };

export const OriginSectionEditorAccordion = ({
  originSection,
  originSectionOrder,
  originSectionWithOrderId,
  onSectionAccordionOpen,
}: props) => {
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [isAccordionOpened, setIsAccordionOpened] = useState<boolean>(false);
  const [sectionName, setSectionName] = useState<string>(originSection.name);
  const [isSectionDisabled, setIsSectionDisabled] = useState<boolean>(false);

  const dispatch = useDispatch();

  const sectionBlocks = originSection?.originBlocks ?? [];

  useEffect(() => {
    if (originSection) {
      if (originSection.name.includes('Greeting')) {
        setSectionName(translate('greeting'));
        setIsSectionDisabled(true);
      }
    }
  }, [originSection, originSectionOrder]);

  useEffect(() => {
    onSectionAccordionOpen(isAccordionOpened, originSectionWithOrderId);
  }, [isAccordionOpened]);

  const onDragEnd = useCallback(
    (result: DragResult) => {
      if (!result.destination) {
        return;
      }

      const startIndex = result.source.index;
      const endIndex = result.destination.index;

      dispatch(
        updateCurrentSubmissionOriginSectionBlocksOrder(
          startIndex,
          endIndex,
          originSectionWithOrderId
        )
      );
    },
    [dispatch, originSectionWithOrderId]
  );

  const handleEditDoneToggle = useCallback(() => {
    setIsEditMode(false);
    if (sectionName !== originSection.name) {
      const actionPayload: UpdateCurrentSubmissionOriginSectionNameActionPayloadType = {
        name: sectionName,
        originSectionWithOrderId: originSectionWithOrderId,
      };
      dispatch(updateCurrentSubmissionOriginSectionName(actionPayload));
    }
  }, [dispatch, originSectionWithOrderId, originSection.name, sectionName]);

  const handleSectionBlocksFormUpdate = useCallback(
    (
      originSectionBlockWithOrderId: string,
      updatedBlockFields: Partial<OriginBlockEntityInterface>,
      valueKey: OriginBlockFormValueKey
    ) => {
      dispatch(
        updateCurrentSubmissionOriginSectionBlock(
          originSectionWithOrderId,
          originSectionBlockWithOrderId,
          updatedBlockFields,
          valueKey
        )
      );
    },
    [originSectionWithOrderId, dispatch]
  );

  const handleDeleteSection = () => {
    dispatch(deleteCurrentSubmissionOriginSection(originSection.id));
  };

  const handleSectionBlocksFormDelete = (originBlockId: string) => {
    dispatch(deleteCurrentSubmissionOriginSectionBlock(originSectionWithOrderId, originBlockId));
  };

  const handleSectionBlocksFormCreate = () => {
    dispatch(createSubmissionOriginSectionBlock(originSectionWithOrderId));
  };

  return (
    <FormGroup className="origin-section-container">
      <Accordion disabled={isSectionDisabled} expanded={isAccordionOpened}>
        <Box sx={boxStyles} className="accordion-summary-container">
          <MuiButton
            disabled={isSectionDisabled}
            onClick={handleDeleteSection}
            size="large"
            disableRipple
            data-testid={`summary-${originSection.name}-delete-button`}
          >
            <TrashIcon />
          </MuiButton>
          <AccordionSummary
            data-testid={`summary-${originSection.name}`}
            onClick={() =>
              setIsAccordionOpened(isEditMode ? isAccordionOpened : !isAccordionOpened)
            }
            expandIcon={isEditMode ? null : <ExpandMore />}
            className={cx({ 'summary-edit-mode': isEditMode })}
            classes={accordionClasses}
          >
            {isEditMode ? (
              <TextField
                data-testid={`summary-input-${originSection.name}`}
                value={sectionName}
                className="summary-input"
                onChange={(e) => {
                  setSectionName(e.target.value);
                }}
              />
            ) : (
              <div data-testid={`summary-label-${originSection.name}`} className="summary-content">
                {sectionName}
              </div>
            )}
          </AccordionSummary>
          <span className="summary-edit-control">
            {isEditMode ? (
              <MuiButton
                data-testid={`summary-${originSection.name}-edit-done-button`}
                size="large"
                disableRipple
                onClick={handleEditDoneToggle}
              >
                <WellDone />
              </MuiButton>
            ) : (
              <MuiButton
                size="large"
                disabled={isSectionDisabled}
                disableRipple
                onClick={() => setIsEditMode(true)}
                data-testid={`summary-${originSection.name}-edit-button`}
              >
                <EditSmallIcon />
              </MuiButton>
            )}
          </span>
        </Box>
        <AccordionDetails
          classes={formContentClasses}
          data-testid={`${originSection.name}-${formContentClasses.root}`}
        >
          <div className="multiple-block-buttons">
            <Button
              testId={`${originSection.name}-add-block-button`}
              onClick={handleSectionBlocksFormCreate}
              endIcon={<Add />}
              type="tertiary"
            >
              {translate('add')}
            </Button>
          </div>
          <DragDropArea<OriginSectionEntityBlocksOriginBlockEntityInterface>
            droppableId="section-editor-form"
            onDragEnd={onDragEnd}
            items={sectionBlocks}
          >
            {({ id, originBlock }) => (
              <OriginSectionBlockFormWrapper
                id={id}
                originBlock={originBlock}
                handleSectionBlocksFormUpdate={handleSectionBlocksFormUpdate}
                handleSectionBlocksFormDelete={handleSectionBlocksFormDelete}
              />
            )}
          </DragDropArea>
        </AccordionDetails>
      </Accordion>
    </FormGroup>
  );
};

interface OriginSectionBlockFormWrapperProps {
  id: string;
  originBlock: OriginBlockEntityInterface;
  handleSectionBlocksFormUpdate: (
    originSectionBlockWithOrderId: string,
    updatedOriginBlock: Partial<OriginBlockEntityInterface>,
    valueKey: OriginBlockFormValueKey
  ) => void;
  handleSectionBlocksFormDelete: (originSectionBlockWithOrderId: string) => void;
}

const OriginSectionBlockFormWrapper = ({
  id,
  originBlock,
  handleSectionBlocksFormUpdate,
  handleSectionBlocksFormDelete,
}: OriginSectionBlockFormWrapperProps) => {
  const onFormChange = useCallback(
    (updatedFormValues: OriginSectionEditorFormValues, valueKey: OriginBlockFormValueKey) => {
      handleSectionBlocksFormUpdate(id, updatedFormValues, valueKey);
    },
    [handleSectionBlocksFormUpdate, id]
  );

  return (
    <div className="section-form" data-testid="section-blocks-form">
      <OriginSectionEditorForm
        title={originBlock.title}
        link={originBlock.link}
        description={originBlock.description}
        contactsType={originBlock.contactsType}
        hasBottomBorder={originBlock.hasBottomBorder}
        onFormChange={onFormChange}
      />
      <div className="section-form-actions">
        <div className="drag-handle">
          <DragDrop />
        </div>
      </div>
      <MultipleRemoveButton handleDelete={handleSectionBlocksFormDelete} id={originBlock.id} />
    </div>
  );
};
